/**
 * PortsProvider — composition-root React component (T015).
 *
 * Canonical behavioural contract: `specs/011-pwa-prototype-convergence/contracts/ports-provider.md`.
 *
 * The single affordance for adapter replacement. `main.tsx` (T024) composes
 * demo ports and passes them as the `ports` prop; tests (T016 harness) pass
 * overridden ports via `renderAppWithPorts({ ... })`. The production tree
 * mounts exactly one `PortsProvider` — nested re-providers to override one
 * port for a subtree are NOT a pattern in this codebase (contracts §1).
 *
 * The component itself is intentionally trivial: a one-line wrapper around
 * `<PortsContext.Provider>`. Port-construction and lifecycle logic lives in
 * the per-port factory hooks `./useSessionPort.ts` and `./useLanguagePort.ts`
 * colocated with this file. Consumer hooks stay in their domain directories
 * — `../session/useSession.ts`, `../i18n/useLanguage.ts`,
 * `../i18n/useTranslate.ts`, `../content/hooks/useContent.ts`. This
 * split keeps the composition readable (build ports → pass to provider) and
 * keeps tests honest (harness builds its own port composition; no magic
 * module replacement).
 *
 * Stable-instance contract (contracts §2): the `ports` argument SHOULD be
 * referentially stable across renders so hook consumers can rely on
 * identity for effect dependencies. The factory hooks honour this —
 * `useSessionPort` stores the adapter in `useState(() => new ...)[0]`, and
 * `useLanguagePort` returns a `useMemo` port whose identity changes only
 * when `active` does. The composer (`main.tsx`) wraps the `ports` object
 * itself in `useMemo` so its identity matches the stable slots.
 */

import type { ReactNode } from 'react';

import { type Ports, PortsContext } from './PortsContext.js';

export interface PortsProviderProps {
  readonly ports: Ports;
  readonly children: ReactNode;
}

export function PortsProvider({ ports, children }: PortsProviderProps) {
  return <PortsContext.Provider value={ports}>{children}</PortsContext.Provider>;
}
